import React, {useContext, useEffect, useMemo} from "react";
import {useState, useRef} from "react";
import {useNavigate, Navigate} from "react-router-dom";
import useMain from "../../../hooks/useMain";
import {StoreContext} from "../../../store/StoreContext";
import {swalAlert, toast} from "../../../alerts/sweetalert";
import {COLOR_DANGER} from "../../../constants/colors";
import DataTable from "react-data-table-component";
import {LIMIT_SAVEDFORMS} from "../../../constants";
import Paginator from "../../../components/paginator/Paginator";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowRotateRight, faEye, faPenToSquare, faTrash} from "@fortawesome/free-solid-svg-icons";
import DialogPreviewFinding from "../../../components/dialog-preview-finding/DialogPreviewFinding";
import DialogPreviewCommitment from "../../../components/dialog-preview-commitment/DialogPreviewCommitment";
import {getLocal, putLocal} from "../../../helpers/local-storage";
import {getCenters} from "../../../mongo-db/centers";

const paginationComponentOptions = {
  rowsPerPageText: "Filas por página",
  rangeSeparatorText: "de",
  selectAllRowsItem: false,
  selectAllRowsItemText: "Todos",
};

const STORAGE_KEY = "allInspections_filters";

const AllInspections = () => {
  const {setAppTitle, session, allInspections, allInspectionsPending} =
    useContext(StoreContext);
  const [cachedAreas, setCachedAreas] = useState(() => getLocal("sso_areas_cache", []));
  const navigate = useNavigate();
  const {fetchAllSavedForms, deleteForm} = useMain();

  // Centros asignados del usuario (solo aplica a perfil 2)
  const profile = session?.profile;
  const _assigned = session?.assignedCenters || [];
  const hasRestriction = profile === 2 && _assigned.length > 0 && _assigned.some(ac => ac.instalacion);
  const assignedCenterNames = useMemo(() => {
    if (!hasRestriction) return "";
    return _assigned.map(ac => ac.instalacion).filter(Boolean).join(",");
  }, [hasRestriction, _assigned]);

  // Restaurar estado guardado si existe (la limpieza se hace en el unmount)
  const saved = useRef((() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })()).current;

  const [search, setSearch] = useState(saved?.search || "");
  const [debouncedSearch, setDebouncedSearch] = useState(saved?.search || "");
  const [fromDate, setFromDate] = useState(saved?.fromDate || "");
  const [toDate, setToDate] = useState(saved?.toDate || "");
  const [templateId, setTemplateId] = useState(null);
  const [openingFinding, setOpeningFinding] = useState(false);
  const [formId, setFormId] = useState(null);
  const [commitTemplateId, setCommitTemplateId] = useState(null);
  const [openingCommitment, setOpeningCommitment] = useState(false);
  const [commitFormId, setCommitFormId] = useState(null);
  const [currentPage, setCurrentPage] = useState(saved?.currentPage || 1);
  const [rowsPerPage, setRowsPerPage] = useState(saved?.rowsPerPage || LIMIT_SAVEDFORMS);
  const [totalRows, setTotalRows] = useState(0);
  const [ready, setReady] = useState(false);
  const [sortBy, setSortBy] = useState(saved?.sortBy || "FechaFormulario");
  const [sortDir, setSortDir] = useState(saved?.sortDir || "desc");
  const [contentFilter, setContentFilter] = useState(saved?.contentFilter || "");
  const [centroFilter, setCentroFilter] = useState(saved?.centroFilter || "");
  const [tableKey, setTableKey] = useState(0);

  const instalacionOptions = useMemo(() => {
    const set = new Set();
    const assignedSet = hasRestriction
      ? new Set(_assigned.map(ac => (ac.instalacion || "").toLowerCase().trim()))
      : null;
    cachedAreas.forEach(area => {
      if (!area?.centers) return;
      area.centers.forEach(c => {
        if (!c?.title) return;
        if (assignedSet && !assignedSet.has(c.title.toLowerCase().trim())) return;
        set.add(c.title);
      });
    });
    return [...set].sort((a, b) => a.localeCompare(b, "es"));
  }, [cachedAreas, hasRestriction, _assigned]);

  // Flag para indicar que la navegacion fue hacia un detalle (no limpiar sessionStorage)
  const navigatingToDetailRef = useRef(false);

  // Persistir filtros en sessionStorage en cada cambio de estado
  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      search, fromDate, toDate, currentPage, rowsPerPage, sortBy, sortDir, contentFilter, centroFilter,
    }));
  }, [search, fromDate, toDate, currentPage, rowsPerPage, sortBy, sortDir, contentFilter, centroFilter]);

  const reloadAllSavedForms = async (
    page = currentPage,
    limit = rowsPerPage
  ) => {
    const pagination = await fetchAllSavedForms({
      page,
      limit,
      sortBy,
      sortDir,
      search: debouncedSearch,
      contentFilter,
      centerName: centroFilter,
      centerNames: assignedCenterNames,
      fromDate,
      toDate,
    });

    const pageAux = Number(pagination?.page) || page;
    const limitAux = Number(pagination?.limit) || limit;
    const totalAux = Number(pagination?.total) || 0;

    setCurrentPage(pageAux);
    setRowsPerPage(limitAux);
    setTotalRows(totalAux);
    if (!ready) setReady(true);
  };

  const goToPreviewForms = (IdForm, formType) => {
    navigatingToDetailRef.current = true;
    const rol = "";
    navigate(
      `/cermaq/roles/${rol ? rol + "/" : ""}forms/preview/${formType}/${IdForm}`
    );
  };

  const previewFinding = (fId, formType) => {
    if (openingFinding) return;
    setTemplateId(formType);
    setFormId(fId);
  };

  const closeFinding = () => {
    if (openingFinding) return;
    setTemplateId(null);
    setFormId(null);
  };

  const previewCommitment = (fId, formType) => {
    if (openingCommitment) return;
    setCommitTemplateId(formType);
    setCommitFormId(fId);
  };

  const closeCommitment = () => {
    if (openingCommitment) return;
    setCommitTemplateId(null);
    setCommitFormId(null);
  };

  const goToForms = (IdForm, formType) => {
    navigatingToDetailRef.current = true;
    navigate(`/cermaq/roles/forms/${formType}/${IdForm}`);
  };

  const remove = async fId => {
    const {isConfirmed} = await swalAlert(
      "¿Deseas eliminar esta inspección?",
      null,
      "Eliminar",
      "Cancelar",
      COLOR_DANGER
    );
    if (!isConfirmed) return;

    await deleteForm(fId);
    await reloadAllSavedForms(currentPage, rowsPerPage);
    toast({icon: "success", title: "Inspección eliminada."});
  };

  useEffect(() => {
    if (navigator.onLine) {
      getCenters().then(data => {
        if (data?.length) {
          putLocal("sso_areas_cache", data);
          setCachedAreas(data);
        }
      }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    setAppTitle("Todas las Inspecciones");
    // Limpiar sessionStorage al desmontar SOLO si no estamos navegando a un detalle.
    // Asi, al volver de Ver/Editar se restauran los filtros,
    // pero al ir a otra seccion se empieza limpio.
    return () => {
      if (!navigatingToDetailRef.current) {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    };
  }, []);

  useEffect(() => {
    reloadAllSavedForms(currentPage, rowsPerPage);
  }, [currentPage, rowsPerPage, debouncedSearch, sortBy, sortDir, contentFilter, centroFilter, fromDate, toDate]);

  const searchInitRef = useRef(true);
  useEffect(() => {
    if (searchInitRef.current) {
      searchInitRef.current = false;
      return; // No debounce en mount/restore
    }
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
      setTableKey(k => k + 1);
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);

  const data = useMemo(() => {
    return allInspections.map(inspection => ({
      ...inspection,
      Titulo: inspection.Titulo
        .replace("Inspector", "SSO")
        .replace("Jefatura", "Supervisor"),
      __instalacion: inspection.centerName || "",
    }));
  }, [allInspections]);

  const columns = [
    {
      id: "nombre",
      name: "Nombre",
      selector: row => row.Titulo,
      width: "25%",
      sortable: true,
      sortField: "Titulo",
    },
    {
      id: "instalacion",
      name: "Centro",
      selector: row => row.__instalacion,
      width: "150px",
      sortable: true,
      sortField: "__instalacion",
    },
    {
      id: "fecha",
      name: "Fecha Ingreso",
      selector: row => row.FechaFormulario,
      width: "200px",
      sortable: true,
      sortField: "FechaFormulario",
    },
    {
      id: "inspector",
      name: "Inspector",
      selector: row => row.username,
      width: "300px",
      sortable: true,
      sortField: "username",
    },
    {
      name: "Acciones",
      width: session?.profile === 1 ? "320px" : "250px",
      cell: row => (
        <div className="d-flex align-items-center">
          <button
            type="button"
            className="btn btn-sm mx-1 bg-primary text-white fs-6"
            onClick={() => goToPreviewForms(row.IdForm, row.Tipo)}
            title="Ver"
          >
            <FontAwesomeIcon icon={faEye} />
          </button>
          <button
            type="button"
            className="btn btn-sm mx-1 bg-primary text-white fs-6"
            onClick={() => goToForms(row.IdForm, row.Tipo)}
            title="Editar"
          >
            <FontAwesomeIcon icon={faPenToSquare} />
          </button>
          {row.hallazgos?.length > 0 && (
            <button
              type="button"
              className="btn btn-sm text-nowrap mx-1 bg-primary text-white fs-6"
              onClick={() => previewFinding(row.IdForm, row.Tipo)}
            >
              Hallazgos
            </button>
          )}
          {row.compromisos?.length > 0 && (
            <button
              type="button"
              className="btn btn-sm text-nowrap mx-1 bg-primary text-white fs-6"
              onClick={() => previewCommitment(row.IdForm, row.Tipo)}
            >
              Compromisos
            </button>
          )}
          {session?.profile === 1 && (
            <button
              type="button"
              className="btn btn-sm mx-1 bg-danger text-white fs-6"
              onClick={() => remove(row.IdForm)}
              title="Eliminar"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          )}
        </div>
      ),
    },
  ];

  // Solo perfiles 1 y 2 pueden acceder
  if (profile !== 1 && profile !== 2) {
    return <Navigate to="/cermaq/" replace />;
  }

  return (
    <>
      <DialogPreviewFinding
        templateId={templateId}
        formId={formId}
        close={closeFinding}
        opening={openingFinding}
        setOpening={setOpeningFinding}
      />
      <DialogPreviewCommitment
        templateId={commitTemplateId}
        formId={commitFormId}
        close={closeCommitment}
        opening={openingCommitment}
        setOpening={setOpeningCommitment}
      />

      <div className="row">
        <div className="col-12 col-md-3 d-flex mb-2">
          <button
            type="button"
            className="btn bg-success text-white fw-semibold text-nowrap me-3 my-auto"
            onClick={() => reloadAllSavedForms(1, rowsPerPage)}
            disabled={!session || allInspectionsPending}
          >
            <FontAwesomeIcon icon={faArrowRotateRight} className="me-2" />
            Refrescar
          </button>
        </div>

        <div className="col-12 col-md-5">
          <div className="row">
            <div className="col-6 mb-2">
              <label htmlFor="date-from-filter" className="form-label">
                Desde
              </label>
              <input
                id="date-from-filter"
                type="date"
                className="form-control"
                max={toDate}
                value={fromDate}
                onChange={({target}) => { setFromDate(target.value); setCurrentPage(1); setTableKey(k => k + 1); }}
                placeholder="Selecciona fecha"
                aria-label="Selecciona fecha"
                aria-describedby="addon-wrapping"
              />
            </div>
            <div className="col-6 mb-2">
              <label htmlFor="date-to-filter" className="form-label">
                Hasta
              </label>
              <input
                id="date-to-filter"
                type="date"
                className="form-control"
                min={fromDate}
                value={toDate}
                onChange={({target}) => { setToDate(target.value); setCurrentPage(1); setTableKey(k => k + 1); }}
                placeholder="Selecciona fecha"
                aria-label="Selecciona fecha"
                aria-describedby="addon-wrapping"
              />
            </div>
          </div>
        </div>

        <div className="col-6 col-md-2 mb-2">
          <label htmlFor="instalacion-filter" className="form-label">
            Centro
          </label>
          <select
            id="instalacion-filter"
            className="form-select"
            value={centroFilter}
            onChange={({target}) => {
              setCentroFilter(target.value);
              setCurrentPage(1);
              setTableKey(k => k + 1);
            }}
          >
            <option value="">Todos</option>
            {instalacionOptions.map(name => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-6 col-md-2 mb-2">
          <label htmlFor="content-filter" className="form-label">
            Contenido
          </label>
          <select
            id="content-filter"
            className="form-select"
            value={contentFilter}
            onChange={({target}) => {
              setContentFilter(target.value);
              setCurrentPage(1);
              setTableKey(k => k + 1);
            }}
          >
            <option value="">Todos</option>
            <option value="hallazgos">Con Hallazgos</option>
            <option value="compromisos">Con Compromisos</option>
          </select>
        </div>

        <div className="col-12 col-md-2 mb-2">
          <label htmlFor="search" className="form-label">
            Buscar
          </label>
          <input
            id="search"
            type="search"
            className="form-control"
            placeholder="Buscar por nombre, Inspector..."
            value={search}
            onChange={({target}) => setSearch(target.value)}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="table-responsive">
            <DataTable
              key={tableKey}
              className="table"
              columns={columns}
              data={data}
              progressPending={!ready || allInspectionsPending}
              pagination
              paginationServer
              sortServer
              paginationTotalRows={totalRows}
              paginationPerPage={rowsPerPage}
              paginationDefaultPage={currentPage}
              onChangePage={page => setCurrentPage(page)}
              onChangeRowsPerPage={perPage => { setRowsPerPage(perPage); setCurrentPage(1); }}
              onSort={(column, direction) => {
                const newBy = column.sortField || "FechaFormulario";
                const newDir = direction === "asc" ? "asc" : "desc";
                if (newBy === sortBy && newDir === sortDir) return;
                setSortBy(newBy);
                setSortDir(newDir);
                setCurrentPage(1);
              }}
              paginationRowsPerPageOptions={[10]}
              noDataComponent={
                <div className="mt-5">Sin datos para mostrar</div>
              }
              paginationComponentOptions={paginationComponentOptions}
              paginationComponent={Paginator}
              defaultSortFieldId="fecha"
              defaultSortAsc={false}
              progressComponent={
                <div className="text-center text-primary-cermaq mt-5">
                  <div className="spinner-border" role="status" />
                </div>
              }
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default AllInspections;
